import Flex from "../../components/Flex";
import { KeyValPair, ReportContainer, checkNull } from "../constant";
import { NestedText, SubSection } from "./TherapyBlocks";

/** Psychoeducation fields this block reads off the visit note form data. */
export interface PsychoeducationData {
  psychoeducationSessionRequired?: boolean;
  primaryTopic?: string;
  learningObjectives?: string;
  contentDelivered?: string;
  skillsCategoriesCovered?: string;
  skillsDescription?: string;
  materialsDistributed?: string;
  materialDetails?: string;
  engagementLevel?: string;
  comprehensionLevel?: string;
  questionsDiscussion?: string;
  participantFeedback?: string;
  inSessionPractice?: string;
  homePracticeAssignments?: string;
  realLifeApplicationPlan?: string;
  followUpTopics?: string;
  additionalResourcesNeeded?: string;
  nextSessionPlan?: string;
  psychoeducationProviderNote?: string;
}

interface PsychoeducationBlockProps {
  data?: PsychoeducationData;
  /** When true, renders nothing if `psychoeducationSessionRequired` is falsy. */
  gated?: boolean;
}

const splitCsv = (val?: string): string[] =>
  (val ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Renders the Psychoeducation Therapy section used inside several visit note
 * summaries (Psychiatric Note, Psychotherapy Note, etc.).
 */
const PsychoeducationBlock = ({
  data,
  gated = true,
}: PsychoeducationBlockProps) => {
  const required = !!data?.psychoeducationSessionRequired;
  if (gated && !required) return null;

  const skills = splitCsv(data?.skillsCategoriesCovered);
  const materials = splitCsv(data?.materialsDistributed);

  const content = (
    <>
      {(data?.primaryTopic ||
        data?.learningObjectives ||
        data?.contentDelivered) && (
        <SubSection label="Educational Topic & Content">
          {data?.primaryTopic && (
            <KeyValPair k="Primary Topic" v={data.primaryTopic} />
          )}
          <NestedText
            label="Learning Objectives"
            val={data?.learningObjectives}
          />
          <NestedText label="Content Delivered" val={data?.contentDelivered} />
        </SubSection>
      )}

      {(checkNull(skills) || data?.skillsDescription) && (
        <SubSection label="Skills & Strategies Taught">
          {checkNull(skills) && (
            <KeyValPair k="Skills Categories Covered" v={skills.join(", ")} />
          )}
          <NestedText
            label="Skills & Strategies Description"
            val={data?.skillsDescription}
          />
        </SubSection>
      )}

      {(checkNull(materials) || data?.materialDetails) && (
        <SubSection label="Materials & Resources Provided">
          {checkNull(materials) && (
            <KeyValPair k="Materials Distributed" v={materials.join(", ")} />
          )}
          <NestedText label="Material Details" val={data?.materialDetails} />
        </SubSection>
      )}

      {(data?.engagementLevel ||
        data?.comprehensionLevel ||
        data?.questionsDiscussion ||
        data?.participantFeedback) && (
        <SubSection label="Participant Engagement & Response">
          {data?.engagementLevel && (
            <KeyValPair k="Overall Engagement Level" v={data.engagementLevel} />
          )}
          {data?.comprehensionLevel && (
            <KeyValPair
              k="Comprehension & Understanding"
              v={data.comprehensionLevel}
            />
          )}
          <NestedText
            label="Questions Asked & Discussion"
            val={data?.questionsDiscussion}
          />
          <NestedText
            label="Participant Feedback"
            val={data?.participantFeedback}
          />
        </SubSection>
      )}

      {(data?.inSessionPractice ||
        data?.homePracticeAssignments ||
        data?.realLifeApplicationPlan) && (
        <SubSection label="Practice & Application">
          <NestedText
            label="In-Session Practice"
            val={data?.inSessionPractice}
          />
          <NestedText
            label="Home Practice Assignments"
            val={data?.homePracticeAssignments}
          />
          <NestedText
            label="Real-Life Application Plan"
            val={data?.realLifeApplicationPlan}
          />
        </SubSection>
      )}

      {(data?.followUpTopics ||
        data?.additionalResourcesNeeded ||
        data?.nextSessionPlan) && (
        <SubSection label="Follow-Up & Recommendations">
          <NestedText
            label="Recommended Follow-Up Topics"
            val={data?.followUpTopics}
          />
          <NestedText
            label="Additional Resources Needed"
            val={data?.additionalResourcesNeeded}
          />
          <NestedText label="Next Session Plan" val={data?.nextSessionPlan} />
        </SubSection>
      )}

      {data?.psychoeducationProviderNote && (
        <SubSection label="Additional Clinical Notes">
          <NestedText
            label="Provider Notes"
            val={data?.psychoeducationProviderNote}
          />
        </SubSection>
      )}
    </>
  );

  // Standalone usage (PSYCHOEDUCATION_THERAPY form type) — skip the outer
  // "Psychoeducation Session" wrapper since the visit note title already
  // identifies it. Gated usage still wraps so the section is distinguishable
  // from its parent form's other sections.
  if (!gated) {
    return (
      <Flex flexDirection={"column"} gap={1} mt={1} p={1}>
        {content}
      </Flex>
    );
  }

  return (
    <ReportContainer label="Psychoeducation Session">
      <Flex flexDirection={"column"} gap={1} mt={1} p={1}>
        {content}
      </Flex>
    </ReportContainer>
  );
};

export default PsychoeducationBlock;
